import { useCallback, useRef, useState } from 'react';
import type { LayoutResult } from '@/layout/types';
import type { MindMapDocument } from '@/model/document';

export type ResizeHandle = 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const MIN_WIDTH = 80;
const MIN_HEIGHT = 30;

interface ResizeState {
  nodeId: string;
  handle: ResizeHandle;
  startClientX: number;
  startClientY: number;
  startWidth: number;
  startHeight: number;
  startX: number;
  startY: number;
}

export interface ResizePreview {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
}

export interface UseNodeResizeResult {
  resizingNodeId: string | null;
  resizeHandlePosition: ResizeHandle | null;
  resizePreviewSize: ResizePreview | null;
  onResizeHandleMouseDown: (nodeId: string, handle: ResizeHandle, e: React.MouseEvent) => void;
  onCanvasMouseMove: (e: React.MouseEvent) => void;
  onCanvasMouseUp: () => void;
  onResizeHandleTouchStart: (nodeId: string, handle: ResizeHandle, e: React.TouchEvent) => void;
  onCanvasTouchMove: (e: React.TouchEvent) => void;
  onCanvasTouchEnd: () => void;
}

/** Whether the handle affects the left edge (moves x). */
function affectsLeft(handle: ResizeHandle): boolean {
  return handle === 'left' || handle === 'top-left' || handle === 'bottom-left';
}

/** Whether the handle affects the top edge (moves y). */
function affectsTop(handle: ResizeHandle): boolean {
  return handle === 'top' || handle === 'top-left' || handle === 'top-right';
}

/** Whether the handle affects the right edge (grows width rightward). */
function affectsRight(handle: ResizeHandle): boolean {
  return handle === 'right' || handle === 'top-right' || handle === 'bottom-right';
}

/** Whether the handle affects the bottom edge (grows height downward). */
function affectsBottom(handle: ResizeHandle): boolean {
  return handle === 'bottom' || handle === 'bottom-left' || handle === 'bottom-right';
}

export function useNodeResize(
  _svgRef: React.RefObject<SVGSVGElement | null>,
  layout: LayoutResult,
  mapDoc: MindMapDocument,
  _panX: number,
  _panY: number,
  zoom: number,
): UseNodeResizeResult {
  const [resizingNodeId, setResizingNodeId] = useState<string | null>(null);
  const [resizeHandlePosition, setResizeHandlePosition] = useState<ResizeHandle | null>(null);
  const [resizePreviewSize, setResizePreviewSize] = useState<ResizePreview | null>(null);

  const stateRef = useRef<ResizeState | null>(null);

  // Shared internal logic: start resize
  const handleResizeStart = useCallback(
    (nodeId: string, handle: ResizeHandle, clientX: number, clientY: number) => {
      const bounds = layout.nodeBounds.get(nodeId);
      if (!bounds) return;

      stateRef.current = {
        nodeId,
        handle,
        startClientX: clientX,
        startClientY: clientY,
        startWidth: bounds.width,
        startHeight: bounds.height,
        startX: bounds.x,
        startY: bounds.y,
      };

      setResizingNodeId(nodeId);
      setResizeHandlePosition(handle);
      setResizePreviewSize({ width: bounds.width, height: bounds.height, offsetX: 0, offsetY: 0 });
    },
    [layout],
  );

  // Shared internal logic: resize move
  const handleResizeMove = useCallback(
    (clientX: number, clientY: number) => {
      const rs = stateRef.current;
      if (!rs) return;

      // Convert client delta to SVG-space delta (account for zoom)
      const deltaX = (clientX - rs.startClientX) / zoom;
      const deltaY = (clientY - rs.startClientY) / zoom;

      let newWidth = rs.startWidth;
      let newHeight = rs.startHeight;
      let offsetX = 0;
      let offsetY = 0;

      // Right edge: drag right increases width
      if (affectsRight(rs.handle)) {
        newWidth = Math.max(MIN_WIDTH, rs.startWidth + deltaX);
      }

      // Bottom edge: drag down increases height
      if (affectsBottom(rs.handle)) {
        newHeight = Math.max(MIN_HEIGHT, rs.startHeight + deltaY);
      }

      // Left edge: drag left increases width, shifts x
      if (affectsLeft(rs.handle)) {
        const candidateWidth = rs.startWidth - deltaX;
        newWidth = Math.max(MIN_WIDTH, candidateWidth);
        // offsetX is how much the left edge moved (negative = moved left = node grew)
        offsetX = -(newWidth - rs.startWidth);
      }

      // Top edge: drag up increases height, shifts y
      if (affectsTop(rs.handle)) {
        const candidateHeight = rs.startHeight - deltaY;
        newHeight = Math.max(MIN_HEIGHT, candidateHeight);
        offsetY = -(newHeight - rs.startHeight);
      }

      setResizePreviewSize({ width: newWidth, height: newHeight, offsetX, offsetY });
    },
    [zoom],
  );

  // Shared internal logic: resize end
  const handleResizeEnd = useCallback(() => {
    const rs = stateRef.current;
    if (rs && resizePreviewSize) {
      const { width, height, offsetX, offsetY } = resizePreviewSize;

      // If the handle moves the left or top edge, update position as well
      if (offsetX !== 0 || offsetY !== 0) {
        mapDoc.setNodePosition(rs.nodeId, rs.startX + offsetX, rs.startY + offsetY);
      }

      mapDoc.setNodeSize(rs.nodeId, width, height);
    }

    stateRef.current = null;
    setResizingNodeId(null);
    setResizeHandlePosition(null);
    setResizePreviewSize(null);
  }, [mapDoc, resizePreviewSize]);

  // Mouse handlers
  const onResizeHandleMouseDown = useCallback(
    (nodeId: string, handle: ResizeHandle, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      handleResizeStart(nodeId, handle, e.clientX, e.clientY);
    },
    [handleResizeStart],
  );

  const onCanvasMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleResizeMove(e.clientX, e.clientY);
    },
    [handleResizeMove],
  );

  const onCanvasMouseUp = useCallback(() => {
    handleResizeEnd();
  }, [handleResizeEnd]);

  // Touch handlers
  const onResizeHandleTouchStart = useCallback(
    (nodeId: string, handle: ResizeHandle, e: React.TouchEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      handleResizeStart(nodeId, handle, touch.clientX, touch.clientY);
    },
    [handleResizeStart],
  );

  const onCanvasTouchMove = useCallback(
    (e: React.TouchEvent) => {
      // Only process single-finger touches (ignore multi-touch / pinch-to-zoom)
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      if (!touch) return;
      handleResizeMove(touch.clientX, touch.clientY);
    },
    [handleResizeMove],
  );

  const onCanvasTouchEnd = useCallback(() => {
    handleResizeEnd();
  }, [handleResizeEnd]);

  return {
    resizingNodeId,
    resizeHandlePosition,
    resizePreviewSize,
    onResizeHandleMouseDown,
    onCanvasMouseMove,
    onCanvasMouseUp,
    onResizeHandleTouchStart,
    onCanvasTouchMove,
    onCanvasTouchEnd,
  };
}
